import { BackendAdapterCube } from "@spawn/backend-adapter-cube";
import { consumer, inject } from "@spawn/core";
import { InitActionExtendedCube } from "@spawn/freegames";
import { ResumeType } from "@spawn/networking";
import { parseNudgeNetworkState } from "@spawn/nudge";
import { parseRevealNetworkState } from "@spawn/reveal";
import { CacheKeys, CacheStorage } from "../../utils/cacheStorage";
import { deepClone } from "../../utils/object";
import { getReelsAfterReveal, invertNudgingOffsets, mapCalaverasToGoldenBonus } from "../hacks";
import { getWonFGScatterCount } from "../helpers";
import { IGameNetworkState } from "../networkState";
import { GameInitResponsesCube } from "../types/cube";
import { UncoverTierService } from "../../services/uncoverTierService";

@consumer
export class ExtendedInitActionCube extends InitActionExtendedCube {
	@inject protected readonly uncoverTierService!: UncoverTierService;
	protected override parse(responses: GameInitResponsesCube, adapter: BackendAdapterCube): IGameNetworkState {
		const gameInit = responses.gameInit.response;
		const result = super.parse(responses, adapter) as IGameNetworkState;
		const isResumingSpin = result.session.resumeType === ResumeType.Spin;

		const tempFeature =
			isResumingSpin && gameInit.transition
				? gameInit.transition.slotGame.nudgeAndReveal
				: gameInit.state.slotGame.nudgeAndReveal;

		tempFeature.nudgingComponent ??= { nudgingOffsets: [] };
		tempFeature.revealComponent ??= { revealedSymbols: [] };
		const uncoverTier = this.uncoverTierService.fromWinFactor(result.slotGameBase.winFactor);

		const feature: Partial<IGameNetworkState> = {
			reveal: mapCalaverasToGoldenBonus(parseRevealNetworkState(tempFeature, uncoverTier)),
			nudging: invertNudgingOffsets(parseNudgeNetworkState(tempFeature)),
			fgTriggerState: tempFeature.triggerSpin
				? {
						reels: tempFeature.triggerSpin.reels,
						reveal: mapCalaverasToGoldenBonus(parseRevealNetworkState(tempFeature.triggerSpin, uncoverTier)),
						nudging: invertNudgingOffsets(parseNudgeNetworkState(tempFeature.triggerSpin)),
					}
				: undefined,
		};

		if (gameInit.isRestore) {
			result.initialResumeReels = getReelsAfterReveal(result.slotGameBase.reels, feature.reveal?.symbols ?? []); // weird and backwards but qa insists on this

			if (result.slotGameFreeGame?.currentRound === 1) {
				// if we are resuming start of freegames, we need to store the response to re-play the normal init flow
				const firstFGResumeResponse = deepClone(responses.gameInit);
				// @ts-ignore
				firstFGResumeResponse.isRestore = false;
				CacheStorage.set(CacheKeys.FirstFGResumeResponse, firstFGResumeResponse);
			} else if (getWonFGScatterCount(feature.reveal) > 0) {
				// if we are resuming mid-freegames, we need to store the reels before the nudge-up animation
				feature.preNudgeUpReels = gameInit.transition?.slotGame.reels;
			}
		}

		return {
			...result,
			...feature,
		};
	}
}
